import { useTimeout } from '@solinkcorp/solink-components';
import { useEffect, useState } from 'react';

/**
 * If `delayMs` is undefined, this function returns false.  If `delayMs` changes
 * to a value, then this function will return false for the next `delayMs`
 * milliseconds, then will return true until `delayMs` becomes undefined again.
 *
 * This can be used to show a loading circle after a short delay.
 *
 * @example
 *
 *     // showLoadingCircle will become true half a second after `loading`
 *     // becomes true.  If `loading` goes back to false in that time, then
 *     // `showLoadingCircle` will never be true.
 *     const loading = isLoading();
 *     const showLoadingCircle = useDelayed(loading ? 500 : undefined);
 */
export function useDelayed(delayMs: number | undefined): boolean {
  const [result, setResult] = useState(false);

  // Use a timeout to set result if it isn't set.
  useTimeout(() => setResult(true), result ? undefined : delayMs);

  // Clear the result when delayMs becomes undefined.
  useEffect(() => {
    if (delayMs === undefined) {
      setResult(false);
    }
  }, [delayMs]);

  return result;
}
